import React from "react";
import { Drawer, Row, Col, Card, Progress, Table, Tag, Empty, Spin, Typography, Grid } from "antd";
import UnmarkedAttendanceAlert from "./UnmarkedAttendanceAlert";
import {
  CalendarOutlined,
  MedicineBoxOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Text } = Typography;

const STATUS_COLORS = {
  approved: "green",
  pending: "orange",
  rejected: "red",
  cancelled: "default",
};

const LEAVE_CARD_META = {
  annual: { icon: <CalendarOutlined />, accent: "#1677ff" },
  sick: { icon: <MedicineBoxOutlined />, accent: "#fa8c16" },
  flexi: { icon: <ThunderboltOutlined />, accent: "#722ed1" },
};

const balanceColor = (quota, balance) => {
  if (!quota) return "#8c8c8c"; // not entitled/configured — neutral, not alarming
  const ratio = balance / quota;
  if (ratio <= 0) return "#cf1322";
  if (ratio <= 0.25) return "#fa8c16";
  return "#237804";
};

const LeaveTypeCard = ({ title, meta, data }) => {
  const quota = data?.quota ?? 0;
  const carryForward = data?.carryForward ?? 0;
  const effectiveQuota = quota + carryForward;
  const used = data?.used ?? 0;
  const pending = data?.pending ?? 0;
  const balance = data?.balance ?? 0;
  const usedPct = effectiveQuota > 0 ? Math.min(((used + pending) / effectiveQuota) * 100, 100) : 0;

  return (
    <Card
      size="small"
      style={{ borderTop: `3px solid ${meta.accent}`, height: "100%" }}
      bodyStyle={{ padding: "12px 14px" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
        <span style={{ color: meta.accent, fontSize: 14 }}>{meta.icon}</span>
        <Text strong style={{ fontSize: 13 }}>{title}</Text>
      </div>

      <div style={{ fontSize: 24, fontWeight: 700, color: balanceColor(effectiveQuota, balance), lineHeight: 1.2 }}>
        {balance}
      </div>
      <Text type="secondary" style={{ fontSize: 11 }}>days remaining</Text>

      <Progress
        percent={usedPct}
        showInfo={false}
        size="small"
        strokeColor={meta.accent}
        trailColor="#f0f0f0"
        style={{ margin: "8px 0 6px" }}
      />

      <div style={{ fontSize: 11, color: "#8c8c8c", lineHeight: 1.6 }}>
        <div>Entitlement {quota}</div>
        {carryForward > 0 && <div>Carried forward {carryForward}</div>}
        <div>Used {used}{pending > 0 ? ` · Pending ${pending}` : ""}</div>
      </div>
    </Card>
  );
};

const EmployeeLeaveSummaryDrawer = ({ open, onClose, employeeName, leaveSummary, loading, isIndia }) => {
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const annualLabel = isIndia ? "Casual Leave" : "Annual Leave";

  const historyColumns = [
    {
      title: "Date",
      dataIndex: "date",
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : "-"),
    },
    { title: "Type", dataIndex: "type" },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (
        <Tag color={STATUS_COLORS[v] || "default"} style={{ textTransform: "capitalize" }}>
          {v}
        </Tag>
      ),
    },
    { title: "Days", dataIndex: "days", align: "center" },
  ];

  const cycleLabel =
    leaveSummary?.cycleStartDate && leaveSummary?.cycleEndDate
      ? `Leave year: ${dayjs(leaveSummary.cycleStartDate).format("DD MMM YYYY")} – ${dayjs(
          leaveSummary.cycleEndDate
        ).format("DD MMM YYYY")}`
      : null;

  return (
    <Drawer
      title={
        <div>
          <div>Leave Summary{employeeName ? ` — ${employeeName}` : ""}</div>
          {cycleLabel && (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              {cycleLabel}
            </Text>
          )}
        </div>
      }
      open={open}
      onClose={onClose}
      width={540}
      styles={{
        body: { padding: "20px 24px" },
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}

    >
      {loading && !leaveSummary ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <UnmarkedAttendanceAlert
            unmarkedAttendance={leaveSummary?.unmarkedAttendance}
            style={{ marginBottom: 16 }}
          />
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={8}>
              <LeaveTypeCard title={annualLabel} meta={LEAVE_CARD_META.annual} data={leaveSummary?.annualLeave} />
            </Col>
            <Col xs={24} sm={8}>
              <LeaveTypeCard title="Sick Leave" meta={LEAVE_CARD_META.sick} data={leaveSummary?.sickLeave} />
            </Col>
            <Col xs={24} sm={8}>
              <LeaveTypeCard title="Flexi Leave" meta={LEAVE_CARD_META.flexi} data={leaveSummary?.flexiLeave} />
            </Col>
          </Row>

          <div style={{ marginTop: 24, marginBottom: 12, fontWeight: 600 }}>Leave History</div>
          {leaveSummary?.history?.length ? (
            <Table
              size="small"
              rowKey={(row, i) => `${row.date}-${i}`}
              columns={historyColumns}
              dataSource={leaveSummary.history}
              pagination={{ pageSize: 10 }}
            />
          ) : (
            <Empty description="No leave records found" />
          )}
        </>
      )}
    </Drawer>
  );
};

export default EmployeeLeaveSummaryDrawer;
